import * as irc from "../lib/irc.js";
import { strip as stripANSI } from "../lib/ansi.js";
import { html } from "../lib/index.js";
import { BufferType, Unread, ServerStatus, getBufferURL, getServerName } from "../state.js";

function UnreadBadge(props) {
	if (props.buffer.unread === Unread.NONE || !props.buffer.unreadCount) {
		return null;
	}
	let count = props.buffer.unreadCount > 99 ? "99+" : props.buffer.unreadCount;
	return html`<span class="unread-badge">${count}</span>`;
}

function BufferItem(props) {
	function handleClick(event) {
		event.preventDefault();
		props.onClick();
	}
	function handleMouseDown(event) {
		if (event.button === 1) { // middle click
			event.preventDefault();
			props.onClose();
		}
	}

	let name = props.buffer.name;
	if (props.buffer.type === BufferType.SERVER) {
		name = getServerName(props.server, props.bouncerNetwork);
	}

	let title;
	let classes = ["type-" + props.buffer.type];
	if (props.active) {
		classes.push("active");
	}
	if (props.buffer.unread !== Unread.NONE) {
		classes.push("unread-" + props.buffer.unread);
	}
	switch (props.buffer.type) {
	case BufferType.SERVER:
		let isError = props.server.status === ServerStatus.DISCONNECTED;
		if (props.bouncerNetwork && props.bouncerNetwork.error) {
			isError = true;
		}
		if (isError) {
			classes.push("error");
		}
		break;
	case BufferType.NICK:
		let user = props.server.users.get(name);
		if (user && irc.isMeaningfulRealname(user.realname, name)) {
			title = stripANSI(user.realname);
		}
		break;
	}

	return html`
		<li class="${classes.join(" ")}" role="tab" aria-selected="${props.active}">
			<a
				href=${getBufferURL(props.buffer)}
				title=${title}
				onClick=${handleClick}
				onMouseDown=${handleMouseDown}
			>${name}<${UnreadBadge} buffer=${props.buffer}/></a>
		</li>
	`;
}

function NetworkGroup(props) {
	let serverBuf = props.buffers[0];
	let channelBufs = props.buffers.slice(1);
	let networkName = getServerName(props.server, props.bouncerNetwork);
	let collapsed = props.collapsedNetworks.has(networkName) && channelBufs.length > 0;

	let toggle = null;
	if (channelBufs.length > 0) {
		toggle = html`
			<button
				type="button"
				class="network-collapse-toggle"
				aria-expanded=${!collapsed}
				aria-label=${collapsed ? "Expand network" : "Collapse network"}
				onClick=${(event) => {
					event.preventDefault();
					props.onToggleCollapse(networkName);
				}}
			>${collapsed ? "▸" : "▾"}</button>
		`;
	}

	let items = collapsed ? [] : channelBufs.map((buf) => html`
		<${BufferItem}
			key=${buf.id}
			buffer=${buf}
			server=${props.server}
			bouncerNetwork=${props.bouncerNetwork}
			onClick=${() => props.onBufferClick(buf)}
			onClose=${() => props.onBufferClose(buf)}
			active=${props.activeBuffer === buf.id}
		/>
	`);

	return html`
		<li class="network-group">
			<div class="network-header">
				${toggle}
				<${BufferItem}
					buffer=${serverBuf}
					server=${props.server}
					bouncerNetwork=${props.bouncerNetwork}
					onClick=${() => props.onBufferClick(serverBuf)}
					onClose=${() => props.onBufferClose(serverBuf)}
					active=${props.activeBuffer === serverBuf.id}
				/>
			</div>
			<ul class="network-channels">
				${items}
			</ul>
		</li>
	`;
}

export default function BufferList(props) {
	// Buffers are already sorted with each network's server buffer first,
	// followed by that network's channels/queries (see compareBuffers in
	// state.js): group consecutive buffers sharing the same server into
	// per-network sections instead of one flat list.
	let groups = [];
	for (let buf of props.buffers.values()) {
		let lastGroup = groups[groups.length - 1];
		if (!lastGroup || lastGroup.server !== buf.server) {
			groups.push({ server: buf.server, buffers: [] });
			lastGroup = groups[groups.length - 1];
		}
		lastGroup.buffers.push(buf);
	}

	let items = groups.map((group) => {
		let server = props.servers.get(group.server);

		let bouncerNetwork = null;
		if (server.bouncerNetID) {
			bouncerNetwork = props.bouncerNetworks.get(server.bouncerNetID);
		}

		return html`
			<${NetworkGroup}
				key=${group.server}
				buffers=${group.buffers}
				server=${server}
				bouncerNetwork=${bouncerNetwork}
				activeBuffer=${props.activeBuffer}
				collapsedNetworks=${props.collapsedNetworks}
				onBufferClick=${props.onBufferClick}
				onBufferClose=${props.onBufferClose}
				onToggleCollapse=${props.onToggleNetworkCollapse}
			/>
		`;
	});

	let addNetworkButton = null;
	if (props.showAddNetwork) {
		addNetworkButton = html`
			<button
				type="button"
				class="add-network-button"
				onClick=${props.onAddNetwork}
			>+ Add network</button>
		`;
	}

	return html`
		<div class="buffer-list-inner">
			<ul role="tablist" aria-label="Buffer list" class="network-list">
				${items}
			</ul>
			${addNetworkButton}
		</div>
	`;
}
